import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Building2, Check } from 'lucide-react';
import { useOrg } from '../contexts/OrgContext';

function OrgSelector() {
  const { currentOrg, organizations, switchOrganization, loading } = useOrg();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading || !currentOrg) {
    return (
      <div style={{
        padding: '0.5rem 1rem',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '0.375rem',
        color: '#94a3b8',
        fontSize: '0.875rem'
      }}>
        Loading...
      </div>
    );
  }

  function handleOrgSwitch(org) {
    switchOrganization(org);
    setIsOpen(false);
  }

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 0.75rem',
          background: 'rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '0.375rem',
          color: '#fff',
          fontSize: '0.875rem',
          fontWeight: '500',
          cursor: 'pointer',
          transition: 'all 0.2s',
          whiteSpace: 'nowrap'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
        }}
      >
        <Building2 size={16} />
        <span>{currentOrg.organization?.name || 'Select Organization'}</span>
        <ChevronDown size={16} style={{
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s'
        }} />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          bottom: 'calc(100% + 0.5rem)',
          left: 0,
          right: 0,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '0.5rem',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '0.75rem 1rem',
            borderBottom: '1px solid var(--border)',
            fontSize: '0.75rem',
            fontWeight: '600',
            color: 'var(--text-light)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Switch Organization
          </div>

          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {organizations.map((assignment) => {
              const org = assignment.organization;
              const isActive = org?.org_id === currentOrg.organization?.org_id;

              return (
                <button
                  key={org?.org_id}
                  onClick={() => handleOrgSwitch(assignment)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    background: isActive ? 'var(--background)' : 'transparent',
                    color: isActive ? 'var(--primary)' : 'var(--text)',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    textAlign: 'left'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'var(--background)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: isActive ? '600' : '500' }}>
                      {org?.name || 'Unknown Organization'}
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: 'var(--text-light)',
                      marginTop: '0.125rem'
                    }}>
                      {assignment.role?.name || 'No Role'}
                    </div>
                  </div>
                  {isActive && (
                    <Check size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                  )}
                </button>
              );
            })}
          </div>

          {organizations.length === 0 && (
            <div style={{
              padding: '2rem 1rem',
              textAlign: 'center',
              color: 'var(--text-light)',
              fontSize: '0.875rem'
            }}>
              No organizations assigned
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default OrgSelector;
